import type { CompanyAttributeMap } from './company';
import type { Step } from '@/components/Stepper/StepItem';
import type { CycleAudienceCriteriaTypeValue } from '@/constants/cycle-evaluation/CycleAudienceCriteria';
import type { Feedback360SettingsLockValue } from '@/constants/review/Feedback360Settings';
import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type {
    CycleAudienceCriteria,
    CycleAudienceCriteriaFields,
} from '@/types/cycle-evaluation/CycleAudience';
import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';
import type { CycleEvaluatorSelection } from '@/types/cycle-evaluation/CycleEvaluatorSelection';
import type { CompanyAttribute, CompanyManager } from '@/types/directory/Company';
import type { PriorityAttribute } from '@/types/priorityAttributeType';
import type { DraggableItem } from '@amelio/ui-library';

import {
    CycleEvaluationStatus,
    EvaluationType,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleEvaluationSteps,
    type CycleEvaluationStepsType,
} from '@/constants/cycle-evaluation/CycleEvaluationSteps';
import { Feedback360SettingsLock } from '@/constants/review/Feedback360Settings';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';

export interface TransformCritariaToFiltersParams {
    criterias: CycleAudienceCriteriaFields[];
    companyAttributes: CompanyAttributeMap;
}

export interface TransformFiltersToCriteriaParams {
    filters: SelectedFilters;
    criterias: CycleAudienceCriteria[];
    cycleAudienceId: string;
}

interface GetPriorityCriteriaParams {
    cycleEvaluatorSelection?: CycleEvaluatorSelection;
    companyAttributeList: CompanyAttributeMap;
    priority: number;
}

interface TransformCriteriaResult {
    filters: SelectedFilters;
    selectedAttributes: CompanyAttributeMap;
}

const filterKeyToCriteriaType: Record<string, CycleAudienceCriteriaTypeValue> = {
    companyAddresses: 'Company',
    departments: 'Department',
    jobTitles: 'JobTitle',
    employeeLevels: 'EmployeeLevel',
    managers: 'Manager',
    customAttributes: 'CustomAttribute',
};

const criteriaTypeToFilterKey: Record<string, string> = {
    Company: 'companyAddresses',
    Department: 'departments',
    JobTitle: 'jobTitles',
    EmployeeLevel: 'employeeLevels',
    Manager: 'managers',
    CustomAttribute: 'customAttributes',
};

export const priorityTypeToFilterKey: Record<string, string> = {
    SameJobTitle: 'jobTitles',
    SameDepartment: 'departments',
    SameManager: 'managers',
    SameOffice: 'companyAddresses',
    SameEmployeeLevel: 'employeeLevels',
};

export const getCycleEvaluationStepPath = (step: string): string | undefined => {
    switch (step) {
        case CycleEvaluationSteps.DEFINE_AUDIENCE:
            return 'define-audience';
        case CycleEvaluationSteps.SELECT_EVALUATORS:
            return 'select-evaluators';
        case CycleEvaluationSteps.SELECT_TEMPLATE:
            return 'select-template';
        case CycleEvaluationSteps.QUESTIONNAIRE_CREATION:
            return 'questionnaire';
        case CycleEvaluationSteps.SCHEDULE_CREATION:
            return 'schedule-creation';
    }
};

export const getCycleEvaluationStepFromPath = (path: string): string | undefined => {
    switch (path) {
        case 'define-audience':
            return CycleEvaluationSteps.DEFINE_AUDIENCE;
        case 'select-evaluators':
            return CycleEvaluationSteps.SELECT_EVALUATORS;
        case 'select-template':
            return CycleEvaluationSteps.SELECT_TEMPLATE;
        case 'questionnaire':
            return CycleEvaluationSteps.QUESTIONNAIRE_CREATION;
        case 'schedule-creation':
            return CycleEvaluationSteps.SCHEDULE_CREATION;
    }
};

const CYCLE_EVALUATION_STEP_ORDER: CycleEvaluationStepsType[] = [
    CycleEvaluationSteps.DEFINE_AUDIENCE,
    CycleEvaluationSteps.SELECT_EVALUATORS,
    CycleEvaluationSteps.SELECT_TEMPLATE,
    CycleEvaluationSteps.QUESTIONNAIRE_CREATION,
    CycleEvaluationSteps.SCHEDULE_CREATION,
];

export const getCycleEvaluationEditLandingStep = (
    cycleEvaluation: CycleEvaluation,
): CycleEvaluationStepsType => {
    const getTargetStep = () => {
        if (cycleEvaluation.reviewType === EvaluationType.SELF_EVALUATION) {
            return CycleEvaluationSteps.QUESTIONNAIRE_CREATION;
        }
        if (cycleEvaluation.reviewType === EvaluationType.MANAGER_EVALUATION) {
            return CycleEvaluationSteps.SELECT_TEMPLATE;
        }
        return CycleEvaluationSteps.SELECT_EVALUATORS;
    };

    const targetStep = getTargetStep();
    const currentIndex = CYCLE_EVALUATION_STEP_ORDER.indexOf(cycleEvaluation.currentStep);
    const targetIndex = CYCLE_EVALUATION_STEP_ORDER.indexOf(targetStep);

    return currentIndex > targetIndex ? targetStep : cycleEvaluation.currentStep;
};

interface GetCycleEvaluationInitialMaxStepParams {
    cycleEvaluation?: CycleEvaluation;
    steps: Step[];
}

export const getCycleEvaluationInitialMaxStep = ({
    cycleEvaluation,
    steps,
}: GetCycleEvaluationInitialMaxStepParams): number | undefined => {
    if (!cycleEvaluation) {
        return undefined;
    }

    const currentIndex = steps.findIndex((s) => s.id === cycleEvaluation.currentStep);

    return currentIndex >= 0 ? currentIndex : undefined;
};

export const transformCriteriaToFilters = ({
    criterias,
    companyAttributes,
}: TransformCritariaToFiltersParams): TransformCriteriaResult => {
    const filters: SelectedFilters = {
        departments: [],
        employeeLevels: [],
        jobTitles: [],
        companyAddresses: [],
        managers: [],
        customAttributes: [],
    };

    const selectedAttributes: CompanyAttributeMap = {};

    criterias.forEach((criteria) => {
        const filterKey = criteriaTypeToFilterKey[criteria.criteriaType];

        if (filterKey && filters[filterKey]) {
            // Managers use 'name' (string), other attributes use 'names' (Record<string, string>)
            if (filterKey === 'managers') {
                (filters[filterKey] as CompanyManager[]).push({
                    id: criteria.value,
                    name: criteria.value,
                });
            } else {
                (filters[filterKey] as CompanyAttribute[]).push({
                    id: criteria.value,
                    names: { en: criteria.value, fr: criteria.value },
                });
            }

            if (companyAttributes[filterKey] && !selectedAttributes[filterKey]) {
                selectedAttributes[filterKey] = companyAttributes[filterKey];
            }
        }
    });

    return { filters, selectedAttributes };
};

export const transformFiltersToCriteria = ({
    filters,
    criterias,
    cycleAudienceId,
}: TransformFiltersToCriteriaParams): CycleAudienceCriteria[] => {
    const criteriaIdMap = new Map<string, string>();

    criterias.forEach((criteria) => {
        const key = `${criteria.criteriaType}:${criteria.value}`;
        criteriaIdMap.set(key, criteria.id);
    });

    return Object.entries(filters).flatMap(([filterKey, values]) => {
        const criteriaType = filterKeyToCriteriaType[filterKey];

        if (!criteriaType) {
            return [];
        }

        return values
            .filter((value) => value.id)
            .map((value) => {
                const key = `${criteriaType}:${value.id}`;

                return {
                    id: criteriaIdMap.get(key) || '',
                    cycleAudienceId,
                    criteriaType,
                    value: value.id!,
                };
            });
    });
};

export const getPriorityCriteria = ({
    cycleEvaluatorSelection,
    companyAttributeList,
    priority,
}: GetPriorityCriteriaParams): DraggableItem<PriorityAttribute>[] => {
    if (!cycleEvaluatorSelection) {
        return [];
    }

    const criterias = cycleEvaluatorSelection.cycleEvaluatorCriterias.filter(
        (criteria) => criteria.priority === priority,
    );

    return criterias.map((criteria) => {
        const attributeKey = priorityTypeToFilterKey[criteria.type] || criteria.type;
        const attribute = companyAttributeList[attributeKey];

        return {
            id: criteria.id,
            data: {
                id: criteria.id,
                title: attribute?.name,
                iconName: attribute?.iconName,
            },
        };
    });
};

/** Only a 360 has these settings; the other evaluations of a cycle name none. */
export const getCycleEvaluationFeedback360Settings = (
    cycleEvaluation?: CycleEvaluation,
): Pick<CycleEvaluatorSelection, 'feedbackPrivacy' | 'commentRepliesEnabled'> | undefined => {
    const selection = cycleEvaluation?.cycleEvaluatorSelection;
    if (cycleEvaluation?.reviewType !== EvaluationType.EVALUATION_360 || !selection) {
        return undefined;
    }

    return {
        feedbackPrivacy: selection.feedbackPrivacy,
        commentRepliesEnabled: selection.commentRepliesEnabled,
    };
};

interface GetCycleFeedback360SettingsLockParams {
    /** Absent for the legacy one-evaluation cycle, which has no group. */
    group?: CycleEvaluationGroup;
    cycleEvaluation?: CycleEvaluation;
}

/**
 * Why a cycle's 360 confidentiality cannot be changed: it is final from launch on. A cycle is
 * launched once its group leaves the draft, or, without a group, once its own status does.
 */
export const getCycleFeedback360SettingsLock = ({
    group,
    cycleEvaluation,
}: GetCycleFeedback360SettingsLockParams): Feedback360SettingsLockValue | undefined => {
    const isLaunched = group
        ? isCycleEvaluationGroupLaunched(group)
        : cycleEvaluation?.status !== CycleEvaluationStatus.DRAFT;

    return isLaunched ? Feedback360SettingsLock.LAUNCHED : undefined;
};
